/**
 * The counter references (D244) — what the shared condition and effect
 * walks ask of every `counter_at_least`, `adjust_counter` and `set_counter`,
 * wherever a pack writes one; and of the body's own amount, the player's
 * health, which a heal moves and `player_health_at_least` reads.
 *
 * @remarks
 * A leaf of its own, beside `item-condition-walker.ts`, so the reference
 * walkers can call it without a cycle through the counter LAYER
 * (`counter-validation.ts`), which walks the counters' own effects with
 * those same walkers.
 *
 * Three questions, each a defect that would load silently without it:
 *
 * - **The counter is declared.** An undeclared one is a typo, and the engine
 *   would meet it as a corrupt bundle.
 * - **A threshold can both hold and fail.** `counter_at_least k` with `k` at
 *   or below the counter's floor always holds (spell nothing), and above its
 *   ceiling never can — either way the reading says nothing. Whether the
 *   pack can carry the counter across `k` is producibility's question
 *   (`counter-validation.ts`), asked of the same reading.
 * - **A setting lies inside the range.** The effects clamp, and clamping a
 *   typo would hide it: `set_counter … 80` on a canister of twelve is a
 *   mistake, not a full suit.
 *
 * @packageDocumentation
 */

import type { ConditionDefinition, CounterAtLeastCondition, CounterEffect, EffectDefinition, PlayerHealthAtLeastCondition, RestoreHealthEffect } from '@orpheus/content-schema';

import { brokenReference } from './broken-reference.js';
import { type DeclaredIdSets } from './declared-ids.js';
import { type ReferenceBrokenFailure } from './load-failure.js';

/**
 * Walk one `counter_at_least`.
 *
 * @param condition - The read
 * @param conditionPath - Its path, for the failures
 * @param declared - The pack's declared-id universe
 * @param failures - Accumulator
 */
const checkCounterCondition = (
	condition: CounterAtLeastCondition,
	conditionPath: string,
	declared: DeclaredIdSets,
	failures: ReferenceBrokenFailure[],
): void => {
	const counter = declared.counters.get(condition.counterId);

	if (counter === undefined) {
		failures.push(brokenReference(`${conditionPath}.counterId`, condition.counterId, 'condition reads an undeclared counter'));

		return;
	}

	if (condition.minimumCount <= counter.min) {
		failures.push(
			brokenReference(
				`${conditionPath}.minimumCount`,
				condition.counterId,
				`the counter never goes below ${String(counter.min)}, so "at least ${String(condition.minimumCount)}" always holds and says nothing — drop the reading, or ask for more`,
			),
		);
	} else if (condition.minimumCount > counter.max) {
		failures.push(
			brokenReference(
				`${conditionPath}.minimumCount`,
				condition.counterId,
				`the counter never goes above ${String(counter.max)}, so "at least ${String(condition.minimumCount)}" can never hold`,
			),
		);
	}
};

/**
 * Walk one `adjust_counter` or `set_counter`.
 *
 * @param effect - The move
 * @param effectPath - Its path, for the failures
 * @param declared - The pack's declared-id universe
 * @param failures - Accumulator
 */
const checkCounterEffect = (effect: CounterEffect, effectPath: string, declared: DeclaredIdSets, failures: ReferenceBrokenFailure[]): void => {
	const counter = declared.counters.get(effect.counterId);

	if (counter === undefined) {
		failures.push(brokenReference(`${effectPath}.counterId`, effect.counterId, 'effect moves an undeclared counter'));

		return;
	}

	if (effect.type === 'set_counter' && (effect.value < counter.min || effect.value > counter.max)) {
		failures.push(
			brokenReference(
				`${effectPath}.value`,
				effect.counterId,
				`the counter holds ${String(counter.min)} to ${String(counter.max)}, so setting it to ${String(effect.value)} would be clamped — a typo hidden, never a value kept`,
			),
		);
	}
};

/**
 * D244 (Act II §13.2): a `player_health_at_least` — the body's amount, read
 * by the same rule as a counter's: there must be a body, and the threshold
 * must be one its health can reach.
 *
 * @param minimumCount - The threshold (one or more, by the schema)
 * @param thresholdPath - Its path, for the failures
 * @param declared - The pack's declared-id universe
 * @param failures - Accumulator
 */
const checkPlayerHealthThreshold = (minimumCount: number, thresholdPath: string, declared: DeclaredIdSets, failures: ReferenceBrokenFailure[]): void => {
	if (declared.playerHealth === undefined) {
		failures.push(brokenReference(thresholdPath, 'player_health_at_least', 'the pack declares no player body (`manifest.player`), so there is no health to read'));
	} else if (minimumCount > declared.playerHealth) {
		failures.push(brokenReference(thresholdPath, 'player_health_at_least', `the player's health is never above ${String(declared.playerHealth)}, so "at least ${String(minimumCount)}" can never hold`));
	}
};

/** D244: the conditions that read an AMOUNT at a threshold — a counter's, and the body's health. */
export type AmountCondition = CounterAtLeastCondition | PlayerHealthAtLeastCondition;

/** D244: the effects that move an AMOUNT — a counter adjusted or set, the body healed. */
export type AmountEffect = CounterEffect | RestoreHealthEffect;

/**
 * Whether a condition reads an amount.
 *
 * @param condition - Any condition
 * @returns Whether it is a counter's or the body's threshold
 */
export const isAmountCondition = (condition: ConditionDefinition): condition is AmountCondition =>
	condition.type === 'counter_at_least' || condition.type === 'player_health_at_least';

/**
 * Whether an effect moves an amount.
 *
 * @param effect - Any effect
 * @returns Whether it adjusts or sets a counter, or heals the body
 */
export const isAmountEffect = (effect: EffectDefinition): effect is AmountEffect =>
	effect.type === 'adjust_counter' || effect.type === 'set_counter' || effect.type === 'restore_health';

/**
 * Walk one amount read.
 *
 * @param condition - The read
 * @param conditionPath - Its path, for the failures
 * @param declared - The pack's declared-id universe
 * @param failures - Accumulator
 */
export const checkAmountCondition = (condition: AmountCondition, conditionPath: string, declared: DeclaredIdSets, failures: ReferenceBrokenFailure[]): void => {
	if (condition.type === 'counter_at_least') {
		checkCounterCondition(condition, conditionPath, declared, failures);
	} else {
		checkPlayerHealthThreshold(condition.minimumCount, `${conditionPath}.minimumCount`, declared, failures);
	}
};

/**
 * Walk one amount move: a counter must be declared and a setting inside its range; a heal needs a body.
 *
 * @param effect - The move
 * @param effectPath - Its path, for the failures
 * @param declared - The pack's declared-id universe
 * @param failures - Accumulator
 */
export const checkAmountEffect = (effect: AmountEffect, effectPath: string, declared: DeclaredIdSets, failures: ReferenceBrokenFailure[]): void => {
	if (effect.type !== 'restore_health') {
		checkCounterEffect(effect, effectPath, declared, failures);
	} else if (declared.playerHealth === undefined) {
		failures.push(brokenReference(effectPath, 'restore_health', 'the pack declares no player body (`manifest.player`), so a heal has nothing to restore'));
	}
};
